import React, { useState, useEffect, useRef } from 'react'
import { useAuthStore } from '@store'
import apiClient from '@services/api/client'

const NotificationBell = () => {
  const { user } = useAuthStore()
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [showDropdown, setShowDropdown] = useState(false)
  const [loading, setLoading] = useState(false)
  const dropdownRef = useRef(null)
  const isMarkingRef = useRef(false) // Bloquer le rechargement automatique pendant le marquage

  // Fermer le dropdown si on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false)
      }
    }

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showDropdown])

  // Charger les notifications
  const loadNotifications = async () => {
    // Ne pas recharger si on est en train de marquer
    if (isMarkingRef.current) {
      console.log('[NotificationBell] Rechargement bloqué - marquage en cours')
      return
    }
    
    // Vérifier aussi le localStorage
    if (localStorage.getItem('isMarkingNotifications') === 'true') {
      console.log('[NotificationBell] Rechargement bloqué - marquage en cours (localStorage)')
      return
    }

    if (!user?.client_id && !user?.id) {
      console.log('[NotificationBell] Pas de user disponible')
      return
    }

    try {
      console.log('[NotificationBell] ===== CHARGEMENT NOTIFICATIONS =====')

      const result = await apiClient.get('/notifications')

      console.log('[NotificationBell] Réponse complète:', result)

      const notifs = result.data || []
      // Limiter à 10 pour le dropdown
      setNotifications(notifs.slice(0, 10))

      // Utiliser unread_count fourni par l'API ou compter localement
      const unread = result.unread_count !== undefined
        ? result.unread_count
        : notifs.filter(n => n.is_read === 0 || n.is_read === '0' || n.is_read === false).length

      console.log('[NotificationBell] Total notifications:', notifs.length)
      console.log('[NotificationBell] Compteur non lues:', unread)

      setUnreadCount(unread)
    } catch (error) {
      console.error('[NotificationBell] ❌ ERREUR chargement:', error)
    }
  }

  // Charger au montage et toutes les 10 secondes pour une mise à jour rapide
  useEffect(() => {
    // Ne charger que si on a un user
    if (!user?.client_id && !user?.id) {
      console.log('[NotificationBell] Attente du user...')
      return
    }
    
    console.log('[NotificationBell] User détecté, chargement des notifications')
    loadNotifications()
    
    const interval = setInterval(loadNotifications, 15000) // 15 secondes pour détecter les nouvelles notifications sans conflit
    
    // Écouter l'événement de rechargement
    const handleReloadNotifications = () => {
      console.log('[NotificationBell] Rechargement forcé des notifications')
      loadNotifications()
    }
    window.addEventListener('reloadNotifications', handleReloadNotifications)
    
    return () => {
      clearInterval(interval)
      window.removeEventListener('reloadNotifications', handleReloadNotifications)
    }
  }, [user?.client_id, user?.id])

  // Recharger quand la fenêtre redevient active
  useEffect(() => {
    const handleFocus = () => {
      loadNotifications()
    }
    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [user])

  // Marquer une notification comme lue
  const markAsRead = async (notificationId) => {
    try {
      console.log('[NotificationBell] Marquage notification:', notificationId)

      // Bloquer le rechargement pendant le marquage
      isMarkingRef.current = true

      await apiClient.put('/notifications/' + notificationId + '/read')

      console.log('[NotificationBell] Réponse API: success')
      
      // Mettre à jour l'interface avec animation
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: 1, justMarked: true } : n)
      )
      setUnreadCount(prev => Math.max(0, prev - 1))

      // Notifier les autres composants (AdminLayout) de recharger leurs compteurs
      window.dispatchEvent(new CustomEvent('notificationRead'))

      // Débloquer et recharger après 2 secondes
      setTimeout(() => {
        isMarkingRef.current = false
        loadNotifications()
      }, 2000)
    } catch (error) {
      console.error('Erreur marquage notification:', error)
      // En cas d'erreur, recharger pour restaurer l'état correct
      isMarkingRef.current = false
      loadNotifications()
    }
  }

  // Marquer toutes comme lues
  const markAllAsRead = async () => {
    setLoading(true)
    isMarkingRef.current = true // Bloquer le rechargement automatique

    try {
      console.log('[NotificationBell] Marquage toutes comme lues')

      // Sauvegarder le nombre avant marquage
      const previousUnreadCount = unreadCount

      // Bloquer immédiatement AdminLayout via localStorage
      localStorage.setItem('isMarkingNotifications', 'true')
      localStorage.setItem('notificationCount', '0')

      // Marquer chaque notification non lue individuellement
      const unreadNotifs = notifications.filter(n => n.is_read === 0 || n.is_read === '0' || n.is_read === false)
      await Promise.all(unreadNotifs.map(n => apiClient.put('/notifications/' + n.id + '/read')))

      console.log(`✅ ${previousUnreadCount} notification(s) marquée(s) comme lue(s)`)
      
      // Mettre à jour l'interface APRÈS la confirmation de l'API
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })))
      setUnreadCount(0)
      
      // Dispatcher les événements pour compatibilité
      window.dispatchEvent(new CustomEvent('notificationMarkedAllRead'))
      window.dispatchEvent(new Event('storage'))
      
      // Fermer le dropdown après 800ms
      setTimeout(() => {
        setShowDropdown(false)
      }, 800)
      
      // Débloquer après 10 secondes
      setTimeout(() => {
        isMarkingRef.current = false
        localStorage.removeItem('isMarkingNotifications')
        loadNotifications()
      }, 10000)
      
    } catch (error) {
      console.error('Erreur marquage notifications:', error)
      // En cas d'erreur, débloquer et recharger
      isMarkingRef.current = false
      loadNotifications()
    } finally {
      setLoading(false)
    }
  }

  // Formater la date
  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'À l\'instant'
    if (diffMins < 60) return `Il y a ${diffMins} min`
    if (diffHours < 24) return `Il y a ${diffHours}h`
    if (diffDays < 7) return `Il y a ${diffDays}j`
    
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
  }

  // Couleurs du badge
  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-800 border-blue-200',
      green: 'bg-green-100 text-green-800 border-green-200',
      red: 'bg-red-100 text-red-800 border-red-200',
      yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      orange: 'bg-orange-100 text-orange-800 border-orange-200',
      purple: 'bg-purple-100 text-purple-800 border-purple-200',
    }
    return colors[color] || colors.blue
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bouton de notification */}
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2.5 bg-gray-50 hover:bg-gray-100 rounded-md transition-all shadow-sm border border-gray-200"
      >
        <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        
        {/* Badge de notifications non lues */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full shadow-md">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown des notifications */}
      {showDropdown && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-md shadow-xl border border-gray-200 z-50 animate-fadeIn">
          {/* Header */}
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Notifications</h3>
              <p className="text-xs text-gray-500">
                {unreadCount > 0 ? (
                  <span className="text-red-600 font-semibold">{unreadCount} non lue(s)</span>
                ) : (
                  <span className="text-green-600">Aucune notification non lue</span>
                )}
              </p>
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                disabled={loading}
                className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:scale-105 active:scale-95 shadow-sm"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Marquage...
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Tout marquer lu
                  </span>
                )}
              </button>
            )}
          </div>

          {/* Liste des notifications */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <svg className="w-16 h-16 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
                <p className="text-sm">Aucune notification</p>
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  onClick={() => markAsRead(notif.id)}
                  className={`p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${
                    !notif.is_read || notif.is_read === 0 || notif.is_read === false ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {/* Icône */}
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-xl border ${getColorClasses(notif.color)}`}>
                      {notif.icon || '🔔'}
                    </div>

                    {/* Contenu */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="text-sm font-semibold text-gray-900">
                          {notif.title}
                        </h4>
                        {(!notif.is_read || notif.is_read === 0 || notif.is_read === false) && (
                          <span className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-1"></span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{notif.message}</p>
                      <p className="text-xs text-gray-400 mt-2">{formatDate(notif.created_at)}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-3 border-t border-gray-200 text-center">
              <button
                onClick={() => {
                  setShowDropdown(false)
                  // Naviguer vers la page des notifications si elle existe
                }}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Voir toutes les notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default NotificationBell
